import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/site/Seo";
import Band, { Eyebrow } from "@/components/site/Band";
import { submitLead } from "@/lib/api";
import { B340_SPECIALTIES, RPM_SPECIALTIES, BANNED_NOTE, US_STATES } from "@/lib/content";

const PATHS = [
  { id: "340b", label: "340B — specialty drugs" },
  { id: "rpm", label: "RPM — patient visits" },
  { id: "general", label: "Not sure yet" },
];

export default function Contact() {
  const loc = useLocation();
  const initialPath = loc.state?.path || "general";
  const [path, setPath] = useState(initialPath);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    practice_name: "",
    specialty: "",
    contact_name: "",
    email: "",
    phone: "",
    market: loc.state?.market || "",
    panel_size: "",
    annual_drug_spend: "",
    message: "",
    website: "", // honeypot
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const specialtyOptions =
    path === "340b" ? B340_SPECIALTIES : path === "rpm" ? RPM_SPECIALTIES : [...new Set([...B340_SPECIALTIES, ...RPM_SPECIALTIES])];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.practice_name || !form.specialty || !form.contact_name || !form.email) {
      toast.error("Please fill in practice, specialty, contact, and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        practice_name: form.practice_name,
        specialty: form.specialty,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        path,
        source: "web",
        market: form.market,
        panel_size: path === "rpm" && form.panel_size ? parseInt(form.panel_size, 10) : null,
        annual_drug_spend: path === "340b" && form.annual_drug_spend ? parseFloat(form.annual_drug_spend) : null,
        message: form.message,
        website: form.website,
      });
      setDone(true);
      toast.success("Thanks. We'll be in touch.");
    } catch (err) {
      const msg = err?.response?.status === 429 ? "Too many submissions. Please try again later." : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-bone border border-sage/60 rounded-[2px] px-3 py-2.5 text-sm text-ink focus:border-brass outline-none transition-colors";
  const labelCls = "block text-sm font-medium text-spruce mb-1.5";

  return (
    <>
      <Seo
        title="Contact — talk to us about your practice"
        description="Tell us about your practice. We collect practice-level business information only, never patient data."
        path="/contact"
      />

      <Band tone="spruce">
        <Eyebrow>Contact</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mb-6 max-w-3xl">
          Tell us about your practice.
        </h1>
        <p className="text-bone/85 text-base sm:text-lg max-w-2xl" data-testid="contact-privacy-note">
          {BANNED_NOTE} One short form, and a real person reads it.
        </p>
      </Band>

      <Band tone="bone">
        {done ? (
          <div className="max-w-xl border border-sage/60 rounded-[2px] p-8 bg-bone" data-testid="contact-success">
            <h2 className="font-display text-3xl text-spruce mb-3">Received.</h2>
            <p className="text-base text-ink/80">
              Thanks — we have your details and we'll be in touch. Nothing you sent contains patient data,
              and it won't.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="max-w-2xl" data-testid="contact-form" noValidate>
            {/* Path selector */}
            <fieldset className="mb-8">
              <legend className={labelCls}>Which describes your practice?</legend>
              <div className="grid sm:grid-cols-3 gap-2">
                {PATHS.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => { setPath(p.id); setForm((f) => ({ ...f, specialty: "" })); }}
                    data-testid={`contact-path-${p.id}`}
                    className={`text-sm px-3 py-2.5 rounded-[2px] border transition-colors text-left ${
                      path === p.id ? "border-spruce bg-spruce text-bone" : "border-sage/60 text-ink hover:border-spruce"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="practice_name" className={labelCls}>Practice name</label>
                <input id="practice_name" className={inputCls} value={form.practice_name} onChange={set("practice_name")} data-testid="input-practice" />
              </div>
              <div>
                <label htmlFor="specialty" className={labelCls}>Specialty</label>
                <select id="specialty" className={inputCls} value={form.specialty} onChange={set("specialty")} data-testid="input-specialty">
                  <option value="">Select…</option>
                  {specialtyOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="contact_name" className={labelCls}>Your name</label>
                <input id="contact_name" className={inputCls} value={form.contact_name} onChange={set("contact_name")} data-testid="input-name" />
              </div>
              <div>
                <label htmlFor="email" className={labelCls}>Email</label>
                <input id="email" type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="input-email" />
              </div>
              <div>
                <label htmlFor="phone" className={labelCls}>Phone <span className="text-sage font-normal">(optional)</span></label>
                <input id="phone" className={inputCls} value={form.phone} onChange={set("phone")} data-testid="input-phone" />
              </div>

              <div data-testid="field-market">
                <label htmlFor="market" className={labelCls}>Your market (state)</label>
                <select id="market" className={inputCls} value={form.market} onChange={set("market")} data-testid="input-market">
                  <option value="">Select…</option>
                  {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </select>
              </div>

              {/* Path-dependent field */}
              {path === "rpm" && (
                <div data-testid="field-panel">
                  <label htmlFor="panel_size" className={labelCls}>Chronic-condition panel size</label>
                  <input id="panel_size" type="number" min="0" className={`${inputCls} tabular`} value={form.panel_size} onChange={set("panel_size")} data-testid="input-panel" />
                </div>
              )}
              {path === "340b" && (
                <div data-testid="field-drugspend">
                  <label htmlFor="annual_drug_spend" className={labelCls}>Annual specialty drug spend ($)</label>
                  <input id="annual_drug_spend" type="number" min="0" className={`${inputCls} tabular`} value={form.annual_drug_spend} onChange={set("annual_drug_spend")} data-testid="input-drugspend" />
                </div>
              )}
            </div>

            <div className="mt-5">
              <label htmlFor="message" className={labelCls}>Anything else <span className="text-sage font-normal">(optional)</span></label>
              <textarea id="message" rows={4} className={inputCls} value={form.message} onChange={set("message")} data-testid="input-message" />
            </div>

            {/* Honeypot — visually hidden, off-screen */}
            <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
              <label htmlFor="website">Website</label>
              <input id="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
            </div>

            <p className="text-xs text-sage mt-6 mb-4">{BANNED_NOTE}</p>

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 text-sm font-semibold text-spruce bg-brass px-6 py-3 rounded-[2px] hover:brightness-95 transition-all disabled:opacity-60"
              data-testid="contact-submit"
            >
              {submitting ? "Sending…" : "Send"}
            </button>
          </form>
        )}
      </Band>
    </>
  );
}
